'use client';

/**
 * React hook for API access
 * 
 * Single Responsibility: Provide authenticated API client to components.
 * 
 * Usage:
 *   const api = useApi();
 *   const data = await api.get<ProspectSummary>('/api/v1/prospects/123/summary/');
 */

import { useAuth } from '@clerk/nextjs';
import { useMemo } from 'react';
import { createApiClient, type ApiClient } from './api-client';

/**
 * Hook that provides an authenticated API client
 * 
 * The client automatically injects the Clerk JWT into all requests.
 * 
 * @returns API client with get, post, put, patch, delete methods
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const api = useApi();
 *   
 *   const fetchData = async () => {
 *     try {
 *       const summary = await api.get<ProspectSummary>('/api/v1/prospects/123/summary/');
 *       console.log(summary);
 *     } catch (error) {
 *       if (error instanceof ApiError && error.isNotProvisioned) {
 *         // Handle "account not provisioned" case
 *       }
 *     }
 *   };
 * }
 * ```
 */
export function useApi(): ApiClient {
  const { getToken } = useAuth();
  
  // Memoize the client to avoid recreating on every render
  const client = useMemo(() => createApiClient(getToken), [getToken]);
  
  return client;
}

